
import { useState, useMemo, useCallback, useTransition, lazy, Suspense } from 'react';
import type { Itinerary, ItineraryDay, Note } from '@/app/lib/types';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { Button } from '@/components/ui/button';
import { format, addDays, parseISO } from 'date-fns';
import { ItineraryDayView } from './ItineraryDayView';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Edit, Plus, Save, Loader2, CalendarIcon } from 'lucide-react';
import { updateDocumentNonBlocking, useFirestore, useUser, setDocumentNonBlocking } from '@/firebase';
import { doc, collection } from 'firebase/firestore';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { TripPlannerAssistant } from './TripPlannerAssistant';
import { Skeleton } from '../ui/skeleton';

const InteractiveMap = lazy(() => import('../map/InteractiveMap').then(mod => ({ default: mod.InteractiveMap })));

interface ItineraryViewProps {
  itinerary: Itinerary;
  isEditable: boolean;
  preBuiltFormValues?: any; // Used for saving a copy of a pre-built itinerary
}

export function ItineraryView({ itinerary: initialItinerary, isEditable, preBuiltFormValues }: ItineraryViewProps) {
  const [itinerary, setItinerary] = useState(initialItinerary);
  const [selectedDay, setSelectedDay] = useState(itinerary.generatedItinerary.days[0].day);
  const firestore = useFirestore();
  const { user } = useUser();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [isSaving, startSavingTransition] = useTransition();
  const [newStartDate, setNewStartDate] = useState<Date | undefined>(new Date());
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const itineraryRef = useMemo(() => {
    if (!firestore || !itinerary.userProfileId || !itinerary.id || !isEditable) return null;
    // Check if firestore is a mock (for Storybook) by checking for real Firestore methods
    if (!firestore._databaseId) return null;
    return doc(firestore, 'users', itinerary.userProfileId, 'itineraries', itinerary.id);
  }, [firestore, itinerary.userProfileId, itinerary.id, isEditable]);

  const mapLocations = useMemo(() => itinerary.generatedItinerary.days
    .flatMap(day => day.activities.map(activity => ({
      id: activity.locationSlug,
      name: activity.locationName,
      slug: activity.locationSlug,
      latitude: activity.coordinates.lat,
      longitude: activity.coordinates.lng,
    })))
    .filter((loc, index, self) => index === self.findIndex(l => l.id === loc.id)), [itinerary.generatedItinerary.days]);

  const memoizedMap = useMemo(() => (
    <Suspense fallback={<Skeleton className="w-full h-full bg-muted" />}>
      <InteractiveMap locations={mapLocations} />
    </Suspense>
  ), [mapLocations]);

  const updateItineraryInFirestore = (updates: Partial<Itinerary>) => {
    if (!itineraryRef) return;
    updateDocumentNonBlocking(itineraryRef, updates);
  }

  const handleUpdateDayTitle = (dayNumber: number, newTitle: string) => {
    const newDays = itinerary.generatedItinerary.days.map(day =>
      day.day === dayNumber ? { ...day, title: newTitle } : day
    );
    const updatedItinerary = {
      ...itinerary,
      generatedItinerary: { ...itinerary.generatedItinerary, days: newDays }
    };
    setItinerary(updatedItinerary);
    updateItineraryInFirestore({ generatedItinerary: updatedItinerary.generatedItinerary });
  };

  const handleItineraryStartDateChange = (newStartDate: Date | undefined) => {
    if (!newStartDate) return;
    const newDays = itinerary.generatedItinerary.days.map(day => ({
      ...day,
      date: format(addDays(newStartDate, day.day - 1), 'yyyy-MM-dd')
    }));
    const sortedDays = [...newDays].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    const newEndDate = sortedDays[sortedDays.length - 1].date;

    const updatedItinerary: Itinerary = {
      ...itinerary,
      startDate: newStartDate.toISOString(),
      endDate: new Date(newEndDate).toISOString(),
      generatedItinerary: { ...itinerary.generatedItinerary, days: newDays }
    };
    setItinerary(updatedItinerary);
    updateItineraryInFirestore({
      startDate: updatedItinerary.startDate,
      endDate: updatedItinerary.endDate,
      generatedItinerary: updatedItinerary.generatedItinerary
    });
  }

  const handleDayDateChange = (dayNumber: number, newDate: Date) => {
    const newDays = itinerary.generatedItinerary.days.map(day =>
      day.day === dayNumber ? { ...day, date: format(newDate, 'yyyy-MM-dd') } : day
    );
    const sortedDays = [...newDays].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    const newStartDate = sortedDays[0].date;
    const newEndDate = sortedDays[sortedDays.length - 1].date;

    const updatedItinerary: Itinerary = {
      ...itinerary,
      startDate: new Date(newStartDate).toISOString(),
      endDate: new Date(newEndDate).toISOString(),
      generatedItinerary: { ...itinerary.generatedItinerary, days: newDays }
    };
    setItinerary(updatedItinerary);
    updateItineraryInFirestore({
      startDate: updatedItinerary.startDate,
      endDate: updatedItinerary.endDate,
      generatedItinerary: updatedItinerary.generatedItinerary,
    });
  };

  const handleAddDay = () => {
    const sortedDays = [...itinerary.generatedItinerary.days].sort((a, b) => {
      if (!a.date || !b.date) return 0;
      return new Date(a.date).getTime() - new Date(b.date).getTime();
    });

    const lastDay = sortedDays[sortedDays.length - 1];
    const newDate = addDays(parseISO(lastDay.date), 1);
    const newDayNumber = Math.max(...itinerary.generatedItinerary.days.map(d => d.day)) + 1;

    const newDay: ItineraryDay = {
      day: newDayNumber, date: format(newDate, 'yyyy-MM-dd'), title: 'New Day', activities: [], notes: [],
    };

    const newDays = [...itinerary.generatedItinerary.days, newDay];
    const newEndDate = newDate;

    const updatedItinerary: Itinerary = {
      ...itinerary,
      endDate: newEndDate.toISOString(),
      generatedItinerary: { ...itinerary.generatedItinerary, days: newDays },
      duration: itinerary.duration + 1,
    };
    setItinerary(updatedItinerary);
    updateItineraryInFirestore({
      endDate: updatedItinerary.endDate,
      generatedItinerary: updatedItinerary.generatedItinerary,
      duration: updatedItinerary.duration,
    });
  };

  const handleUpdateActivity = (dayNumber: number, activityIndex: number, updatedActivity: ItineraryDay['activities'][0]) => {
    const newDays = itinerary.generatedItinerary.days.map(day => {
      if (day.day === dayNumber) {
        const newActivities = [...day.activities];
        newActivities[activityIndex] = updatedActivity;
        return { ...day, activities: newActivities };
      }
      return day;
    });
    const updatedItinerary = {
      ...itinerary,
      generatedItinerary: { ...itinerary.generatedItinerary, days: newDays }
    };
    setItinerary(updatedItinerary);
    updateItineraryInFirestore({ generatedItinerary: updatedItinerary.generatedItinerary });
  };

  const handleDeleteActivity = (dayNumber: number, activityIndex: number) => {
    const newDays = itinerary.generatedItinerary.days.map(day => {
      if (day.day === dayNumber) {
        const newActivities = day.activities.filter((_, index) => index !== activityIndex);
        return { ...day, activities: newActivities };
      }
      return day;
    });
    const updatedItinerary = {
      ...itinerary,
      generatedItinerary: { ...itinerary.generatedItinerary, days: newDays }
    };
    setItinerary(updatedItinerary);
    updateItineraryInFirestore({ generatedItinerary: updatedItinerary.generatedItinerary });
  };

  const handleAddActivity = (dayNumber: number, newActivity: ItineraryDay['activities'][0]) => {
    const newDays = itinerary.generatedItinerary.days.map(day => {
      if (day.day === dayNumber) {
        return { ...day, activities: [...day.activities, newActivity] };
      }
      return day;
    });
    const updatedItinerary = {
      ...itinerary,
      generatedItinerary: { ...itinerary.generatedItinerary, days: newDays }
    };
    setItinerary(updatedItinerary);
    updateItineraryInFirestore({ generatedItinerary: updatedItinerary.generatedItinerary });
  }

  const handleReorderActivity = useCallback((dayNumber: number, activityIndex: number, direction: 'up' | 'down') => {
    if (!isEditable) return;
    setItinerary(prevItinerary => {
      const dayToUpdate = prevItinerary.generatedItinerary.days.find(d => d.day === dayNumber);
      if (!dayToUpdate) return prevItinerary;

      const activities = Array.from(dayToUpdate.activities);
      const targetIndex = direction === 'up' ? activityIndex - 1 : activityIndex + 1;
      if (targetIndex < 0 || targetIndex >= activities.length) return prevItinerary;

      const [movedItem] = activities.splice(activityIndex, 1);
      activities.splice(targetIndex, 0, movedItem);

      const newDays = prevItinerary.generatedItinerary.days.map(day =>
        day.day === dayNumber ? { ...day, activities: activities } : day
      );
      const updatedItinerary = {
        ...prevItinerary,
        generatedItinerary: { ...prevItinerary.generatedItinerary, days: newDays }
      };
      updateItineraryInFirestore({ generatedItinerary: updatedItinerary.generatedItinerary });
      return updatedItinerary;
    });
  }, [isEditable, updateItineraryInFirestore]);

  const handleUpdateDayNotes = (dayNumber: number, updatedNotes: Note[]) => {
    const newDays = itinerary.generatedItinerary.days.map(day => {
      if (day.day === dayNumber) {
        return { ...day, notes: updatedNotes };
      }
      return day;
    });
    const updatedItinerary = {
      ...itinerary,
      generatedItinerary: { ...itinerary.generatedItinerary, days: newDays }
    };
    setItinerary(updatedItinerary);
    updateItineraryInFirestore({ generatedItinerary: updatedItinerary.generatedItinerary });
  };

  const handleSaveCopy = () => {
    if (!user || !firestore || !firestore._databaseId) {
      // In Storybook or when Firebase is not available, just show a toast
      if (!firestore._databaseId) {
        toast({ title: 'Preview Mode', description: 'In Storybook preview - Firebase operations are disabled.' });
        setIsDialogOpen(false);
        return;
      }
      navigate('/login?redirect_to=/itinerary/generated');
      return;
    }
    if (!newStartDate) {
      toast({ variant: "destructive", title: "No start date selected", description: "Please select a new start date for your itinerary." });
      return;
    }
    startSavingTransition(() => {
      const { preferences, skillLevel, duration, location } = preBuiltFormValues || itinerary;
      const startDate = newStartDate;

      const newItineraryRef = doc(collection(firestore, `users/${user.uid}/itineraries`));

      const updatedDays = itinerary.generatedItinerary.days.map(day => ({
        ...day,
        date: format(addDays(startDate, day.day - 1), 'yyyy-MM-dd')
      }));
      const endDate = addDays(startDate, duration - 1);

      const newItineraryData: Itinerary = {
        ...itinerary,
        id: newItineraryRef.id,
        userProfileId: user.uid,
        name: itinerary.name,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        preferences, skillLevel, duration, location,
        generatedItinerary: { ...itinerary.generatedItinerary, days: updatedDays },
        notes: [], // Start with fresh notes and todos for the copy
        todos: [],
      };

      setDocumentNonBlocking(newItineraryRef, newItineraryData, { merge: false });

      toast({
        title: 'Itinerary Saved!', description: 'A copy has been added to your itineraries.',
        action: (
          <Button asChild variant="outline" size="sm">
            <Link to={`/itinerary/${newItineraryRef.id}`}>View</Link>
          </Button>
        ),
      });
      setIsDialogOpen(false);
      navigate(`/itinerary/${newItineraryRef.id}`);
    });
  };

  const sortedDays = useMemo(() => [...itinerary.generatedItinerary.days].sort((a, b) => {
    if (!a.date || !b.date) return 0;
    return new Date(a.date).getTime() - new Date(b.date).getTime();
  }), [itinerary.generatedItinerary.days]);

  const startDate = parseISO(itinerary.startDate);
  const endDate = parseISO(itinerary.endDate);
  const selectedDayData = itinerary.generatedItinerary.days.find(d => d.day === selectedDay);

  return (
    <div className="w-full max-w-7xl mx-auto -mx-3 sm:mx-auto px-0 sm:px-6 lg:px-8 py-4 sm:py-12 lg:py-16">
      {/* Hero Header Section */}
      <div className="mb-10 animate-fade-in">
        {/* Gradient background container */}
        <div className="relative rounded-none sm:rounded-2xl bg-gradient-to-br from-primary/5 via-accent/5 to-secondary/10 p-4 sm:p-8 shadow-none sm:shadow-loch-lg border-0 sm:border border-primary/10">
          <div className="flex flex-col sm:flex-row justify-between items-start gap-6">
            <div className="flex-1 space-y-4">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight bg-gradient-to-r from-primary via-primary to-accent bg-clip-text text-transparent">
                {itinerary.name}
              </h1>

              <div className="flex flex-col gap-4">
                {/* Date Display with glass effect */}
                <div className="inline-flex items-center gap-3 px-4 py-2.5 rounded-xl bg-background/60 backdrop-blur-sm border border-border/50 shadow-loch w-fit">
                  <CalendarIcon className="h-5 w-5 text-primary flex-shrink-0" />
                  <p className="text-foreground font-semibold text-base">
                    {format(startDate, 'MMM d, yyyy')}
                    <span className="mx-2 text-accent">→</span>
                    {format(endDate, 'MMM d, yyyy')}
                  </p>
                </div>

                {/* Trip Details Tags */}
                <div className="flex flex-wrap gap-2.5">
                  <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-primary/15 to-primary/10 text-primary border border-primary/30 shadow-sm hover:shadow-loch transition-smooth whitespace-normal text-center h-auto">
                    {itinerary.duration} {itinerary.duration === 1 ? 'Day' : 'Days'}
                  </span>
                  <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-secondary/60 to-secondary/40 text-foreground border border-secondary/50 shadow-sm hover:shadow-loch transition-smooth whitespace-normal text-center h-auto">
                    {itinerary.skillLevel}
                  </span>
                  <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-accent/15 to-accent/10 text-accent border border-accent/30 shadow-sm hover:shadow-loch transition-smooth whitespace-normal text-center h-auto">
                    {itinerary.preferences}
                  </span>
                </div>

                {/* Edit Start Date Button */}
                {isEditable && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="gap-2 w-full sm:w-auto touch-target transition-smooth hover:border-primary hover:bg-primary/5">
                        <Edit className="h-4 w-4" /> Edit Start Date
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar mode="single" selected={startDate} onSelect={handleItineraryStartDateChange} initialFocus />
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            </div>

            {/* Save Copy Button */}
            {!isEditable && (
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="w-full sm:w-auto touch-target font-semibold shadow-loch-md hover:shadow-loch-lg hover:scale-105 transition-smooth bg-gradient-to-r from-primary to-primary/90">
                    <Save className="mr-2 h-5 w-5" /> Save a Copy
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle className="text-2xl bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">Save Your Expedition</DialogTitle>
                  </DialogHeader>
                  <div className="flex flex-col items-center gap-4 py-6">
                    <p className="text-muted-foreground text-center">
                      Select a new start date for your personal copy of this itinerary.
                    </p>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full max-w-[280px] justify-start text-left font-normal touch-target hover:border-primary transition-smooth">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {newStartDate ? format(newStartDate, "PPP") : <span>Pick a date</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar mode="single" selected={newStartDate} onSelect={setNewStartDate} initialFocus />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <DialogFooter className="gap-2">
                    <DialogClose asChild>
                      <Button variant="ghost" className="touch-target">Cancel</Button>
                    </DialogClose>
                    <Button onClick={handleSaveCopy} disabled={isSaving} className="touch-target">
                      {isSaving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...</> : 'Save Expedition'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Section */}
      <div className="space-y-10">
        {/* Day Navigation Carousel */}
        <div className="animate-fade-in-up">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">Your Journey</h2>
            <p className="text-sm text-muted-foreground hidden sm:block">Select a day to view details</p>
          </div>

          <Carousel className="w-full">
            <CarouselContent className="-ml-3 sm:-ml-4">
              {sortedDays.map((day, index) => (
                <CarouselItem key={day.day} className="basis-1/2 sm:basis-1/4 md:basis-1/5 lg:basis-1/6 pl-3 sm:pl-4">
                  <Button
                    variant={selectedDay === day.day ? 'default' : 'outline'}
                    className={`
                      w-full h-full min-h-[100px] sm:min-h-[110px] touch-target transition-smooth
                      ${selectedDay === day.day
                        ? 'shadow-loch-lg ring-2 ring-accent/40 scale-105 bg-gradient-to-br from-primary to-primary/90'
                        : 'hover:shadow-loch-md hover:border-accent/50 hover:bg-accent/5'
                      }
                    `}
                    onClick={() => setSelectedDay(day.day)}
                  >
                    <div className="flex flex-col items-center justify-center text-center p-2 sm:p-3">
                      <span className="text-xs sm:text-sm font-semibold opacity-90">
                        {format(parseISO(day.date), 'eee')}
                      </span>
                      <span className="text-3xl sm:text-4xl font-bold my-1">
                        {format(parseISO(day.date), 'd')}
                      </span>
                      <span className="text-xs sm:text-sm font-semibold opacity-90">
                        {format(parseISO(day.date), 'MMM')}
                      </span>
                      <span className="text-[10px] sm:text-xs mt-1 opacity-70">
                        Day {index + 1}
                      </span>
                    </div>
                  </Button>
                </CarouselItem>
              ))}
              {isEditable && (
                <CarouselItem className="basis-1/2 sm:basis-1/4 md:basis-1/5 lg:basis-1/6 pl-3 sm:pl-4">
                  <Button
                    variant="outline"
                    className="w-full h-full min-h-[100px] sm:min-h-[110px] border-2 border-dashed touch-target hover:border-accent hover:bg-accent/10 transition-smooth"
                    onClick={handleAddDay}
                  >
                    <div className="flex flex-col items-center justify-center text-center p-2 sm:p-3">
                      <Plus className="h-8 w-8 sm:h-10 sm:w-10 text-accent" />
                      <span className="text-xs sm:text-sm font-semibold text-accent mt-2">
                        Add Day
                      </span>
                    </div>
                  </Button>
                </CarouselItem>
              )}
            </CarouselContent>
            <CarouselPrevious className="-left-2 sm:-left-4 hidden sm:flex shadow-loch-md hover:shadow-loch-lg transition-smooth" />
            <CarouselNext className="-right-2 sm:-right-4 hidden sm:flex shadow-loch-md hover:shadow-loch-lg transition-smooth" />
          </Carousel>
        </div>


        {/* AI Assistant */}
        {isEditable && (
          <div className="animate-fade-in-up">
            <TripPlannerAssistant
              itinerary={itinerary}
              onAddActivity={handleAddActivity}
            />
          </div>
        )}

        {/* Interactive Map Section */}
        <div className="animate-fade-in-up">
          <div className="mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold mb-2 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">Route Map</h2>
            <p className="text-sm text-muted-foreground">Interactive map showing all locations in your itinerary</p>
          </div>
          <div className="relative h-72 sm:h-80 md:h-96 w-full rounded-2xl overflow-hidden border-2 border-primary/20 shadow-loch-lg ring-1 ring-accent/10">
            {memoizedMap}
          </div>
        </div>
        <div>
          <ItineraryDayView
            dayData={selectedDayData}
            onDateChange={(newDate) => selectedDay && handleDayDateChange(selectedDay, newDate)}
            onUpdateActivity={handleUpdateActivity}
            onDeleteActivity={handleDeleteActivity}
            onAddActivity={handleAddActivity}
            onReorderActivity={handleReorderActivity}
            onUpdateDayTitle={handleUpdateDayTitle}
            onUpdateDayNotes={handleUpdateDayNotes}
            isEditable={isEditable}
          />
        </div>
      </div>
    </div>
  );
}
